import { TopicCommunityHeaderCard } from '@app/components/blocks/card/TopicCommunityHeaderCard'
import { PageLayout } from '@app/pages/layout/PageLayout'
import { useQuery } from '@based/sdk/react'
import { useTranslations } from '@i18n/useLanguage'
import Row from '@app/components/layout/Row'
import useTransform from '@app/util/useTransform'
import Column from '@app/components/layout/Column'
import { TabContent } from './TabContent'
import { HousingFormsCard } from './sections/HousingFormsCard'
import { AmbassadorsCard } from './sections/AmbassadorsCard'
import { ViewTopicCommunityModel } from 'functions/app/topic-community/view'
import useMediaQuery from '@app/util/useMediaQuery'
import { useMemo } from 'react'
import { NotFoundPage } from '@app/pages/error/NotFoundPage'

export type TopicCommunityPageProps = {
  id: string
}

export const TopicCommunityPage = ({ id }: TopicCommunityPageProps) => {
  const t = useTranslations().app
  const transformStyle = useTransform()
  const isMobile = useMediaQuery()
  const query = useQuery('view-topic-community', { id })
  const topicCommunity = query.data as ViewTopicCommunityModel | undefined

  const headerCardProps = useMemo(() => {
    if (!topicCommunity) {
      return null
    }
    return {
      topicCommunityId: topicCommunity.id,
      title: topicCommunity.name,
      bannerImageSrc: topicCommunity.banner?.src,
      followers: (topicCommunity.followers ?? []).map((f) => ({
        firstName: f.firstName ?? '',
        lastName: f.lastName ?? '',
        avatar: f.avatar,
      })),
      managedBy: topicCommunity.manager
        ? {
            title: topicCommunity.manager.title,
            slug: topicCommunity.manager.slug,
            avatar: topicCommunity.manager.avatar,
          }
        : undefined,
    }
  }, [topicCommunity])

  if (query.loading) {
    return <div>{t.common.loading}</div>
  }

  if (!topicCommunity) {
    return <NotFoundPage />
  }

  return (
    <PageLayout gap="2rem">
      <Row
        gap="3rem"
        style={{
          alignItems: 'flex-start',
          ...transformStyle,
        }}
      >
        <Column
          style={{
            flex: '2 1 40rem',
            minWidth: isMobile ? 'auto' : '30rem',
            width: '100%',
          }}
          gap="1.5rem"
        >
          {headerCardProps && (
            <TopicCommunityHeaderCard
              style={{ flex: 1 }}
              {...headerCardProps}
            />
          )}
          <TabContent topicCommunity={topicCommunity} />
        </Column>

        {!isMobile && (
          <Column style={{ flex: '1 1 23rem', minWidth: '20rem' }} gap="1.5rem">
            <HousingFormsCard topicCommunity={topicCommunity} />
            <AmbassadorsCard topicCommunity={topicCommunity} />
          </Column>
        )}
      </Row>
    </PageLayout>
  )
}
